'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { twJoin } from 'tailwind-merge';
import { ColorDetail } from '@/ui/components/pages/ProductDetail/types/ColorDetail';

export interface BundleColorDotsProps {
  colors: ColorDetail[];
  selectedColorName?: string;
  onSelect: (color: ColorDetail) => void;
}

/**
 * Small colour dots for the colour slot of a set card. Plain toggle buttons named after
 * the CMS colour, like the colour picker of the product page. Every dot keeps a grey
 * border so a white dot stays visible on the white card; the selected one gets a dark ring.
 */
export function BundleColorDots({
  colors,
  selectedColorName,
  onSelect,
}: Readonly<BundleColorDotsProps>) {
  const t = useTranslations('BundleVariants');

  return (
    <fieldset
      aria-label={t('color')}
      className="m-0 flex min-w-0 justify-center gap-2 border-0 p-0"
    >
      {colors.map((color) => {
        const isSelected = color.name === selectedColorName;
        // The 50 grey is the lightest neutral whose border still reaches 3:1 (3.95:1) on white,
        // and a white dot on the white card is visible only through its border.
        return (
          <button
            key={color.name}
            type="button"
            aria-pressed={isSelected}
            aria-label={color.name}
            onClick={() => onSelect(color)}
            style={{ backgroundColor: color.hex }}
            className={twJoin(
              'h-4 w-4 rounded-full border border-neutral-50',
              isSelected && 'ring-2 ring-neutral-100 ring-offset-1',
            )}
          />
        );
      })}
    </fieldset>
  );
}
